import client from '@/services/http/client'
import { isMockEnabled, mockDelay, paginate } from '@/services/http/mock'
import type { PaginatedData } from '@/types/api'
import type { SlaEventItem, SlaEventDetail, SlaRuleItem, UpdateSlaRulePayload } from '@/types/sla'

// ============== Mock 数据 ==============
const mockSlaEvents: SlaEventItem[] = [
  {
    id: 'sla-001',
    rule_code: 'customer_confirm_timeout',
    rule_name: '客户确认超时',
    mapping_id: 'map-001',
    task_title: '排查-打印机E-500错误',
    platform_status: 'waiting_customer_confirm',
    target_role: '确认人',
    target_account: 'svc_a',
    first_detected_at: '2026-05-14T14:00:00+08:00',
    last_reminded_at: '2026-05-14T14:30:00+08:00',
    remind_count: 1,
    current_escalation_level: 1,
    level1_sent_at: '2026-05-14T14:30:00+08:00',
    level2_sent_at: null,
    escalation_enabled: true,
    escalation_after_minutes: 120,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    event_status: 'open',
  },
  {
    id: 'sla-002',
    rule_code: 'waiting_start_timeout',
    rule_name: '待接单超时',
    mapping_id: 'map-003',
    task_title: '配置-新增用户权限',
    platform_status: 'waiting_start',
    target_role: '当前执行人',
    target_account: 'tech_a',
    first_detected_at: '2026-05-14T14:00:00+08:00',
    last_reminded_at: null,
    remind_count: 0,
    current_escalation_level: 0,
    level1_sent_at: null,
    level2_sent_at: null,
    escalation_enabled: true,
    escalation_after_minutes: 30,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    event_status: 'open',
  },
  {
    id: 'sla-003',
    rule_code: 'doing_stale',
    rule_name: '处理中超时',
    mapping_id: 'map-002',
    task_title: '修复-数据库连接超时',
    platform_status: 'doing',
    target_role: '当前执行人',
    target_account: 'tech_b',
    first_detected_at: '2026-05-14T12:00:00+08:00',
    last_reminded_at: '2026-05-14T13:30:00+08:00',
    remind_count: 2,
    current_escalation_level: 2,
    level1_sent_at: '2026-05-14T12:30:00+08:00',
    level2_sent_at: '2026-05-14T13:30:00+08:00',
    escalation_enabled: true,
    escalation_after_minutes: 60,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    event_status: 'reminded',
  },
  {
    id: 'sla-004',
    rule_code: 'first_response_timeout',
    rule_name: '首响超时',
    mapping_id: '',
    task_title: '',
    platform_status: '',
    target_role: '接待负责人',
    target_account: 'svc_b',
    first_detected_at: '2026-05-14T08:00:00+08:00',
    last_reminded_at: '2026-05-14T09:00:00+08:00',
    remind_count: 3,
    current_escalation_level: 2,
    level1_sent_at: '2026-05-14T08:30:00+08:00',
    level2_sent_at: '2026-05-14T09:00:00+08:00',
    escalation_enabled: true,
    escalation_after_minutes: 30,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    event_status: 'ignored',
  },
]

const mockSlaDetail: Record<string, SlaEventDetail> = {
  'sla-001': {
    id: 'sla-001',
    rule_code: 'customer_confirm_timeout',
    rule_name: '客户确认超时',
    mapping_id: 'map-001',
    task_title: '排查-打印机E-500错误',
    platform_status: 'waiting_customer_confirm',
    target_role: '确认人',
    target_account: 'svc_a',
    first_detected_at: '2026-05-14T14:00:00+08:00',
    last_reminded_at: '2026-05-14T14:30:00+08:00',
    remind_count: 1,
    current_escalation_level: 1,
    level1_sent_at: '2026-05-14T14:30:00+08:00',
    level2_sent_at: null,
    escalation_enabled: true,
    escalation_after_minutes: 120,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    event_status: 'open',
    remind_history: [
      { channel: 'system', note: '自动提醒：客户确认已超过24小时', created_at: '2026-05-14T14:00:00+08:00' },
    ],
    operator_actions: [
      { action: 'remind', label: '发送提醒', enabled: true },
      { action: 'ignore', label: '忽略事件', enabled: true },
      { action: 'resolve', label: '标记解决', enabled: true },
    ],
    workflow_summary: {
      current_stage: 'ticket_waiting_customer_confirm',
      bottleneck_stage: 'ticket',
      bottleneck_reason_code: 'customer_confirm_timeout',
      bottleneck_reason_text: '客户确认阶段超时，当前闭环停在客户确认。',
      highest_alarm_level: 'L1 一级提醒',
      pending_owner: 'svc_a',
      next_action_label: '联系客户确认处理结果并推进闭环',
    },
    related_entities: {
      message: {
        entity_type: 'message',
        visible: true,
        message_id: 'msg-001',
        room_id: 'room-001',
        room_name: '默认测试群',
        sender_name: '测试发送人',
        message_status: 'pending_response',
      },
      draft: {
        entity_type: 'draft',
        visible: true,
        id: 'draft-001',
        draft_no: 'GD-20260514-0001',
        status: 'confirmed',
        room_id: 'room-001',
        task_title: '排查-打印机-E500错误',
      },
      ticket: {
        entity_type: 'ticket',
        visible: true,
        mapping_id: 'map-001',
        zentao_task_id: 1001,
        platform_status: 'waiting_customer_confirm',
        task_title: '排查-打印机-E500错误',
      },
    },
    quick_links: {
      message: { message_id: 'msg-001' },
      draft: { draft_id: 'draft-001' },
      ticket: { mapping_id: 'map-001' },
      workflow_trace: { entry_type: 'sla-event', entry_id: 'sla-001' },
    },
  },
}

const mockSlaRules: SlaRuleItem[] = [
  {
    id: 1,
    rule_code: 'first_response_timeout',
    rule_name: '首次响应超时',
    stage: 'message',
    threshold_minutes: 30,
    remind_interval_minutes: 30,
    target_role: 'service_owner',
    escalation_enabled: true,
    escalation_after_minutes: null,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    enabled: true,
  },
  {
    id: 2,
    rule_code: 'draft_confirm_timeout',
    rule_name: '草稿确认超时',
    stage: 'draft',
    threshold_minutes: 60,
    remind_interval_minutes: 60,
    target_role: 'service_agent',
    escalation_enabled: true,
    escalation_after_minutes: null,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    enabled: true,
  },
  {
    id: 3,
    rule_code: 'waiting_start_timeout',
    rule_name: '待开始处理超时',
    stage: 'ticket',
    threshold_minutes: 30,
    remind_interval_minutes: 30,
    target_role: 'tech_owner',
    escalation_enabled: true,
    escalation_after_minutes: null,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    enabled: true,
  },
  {
    id: 4,
    rule_code: 'doing_stale',
    rule_name: '处理中停滞',
    stage: 'ticket',
    threshold_minutes: 120,
    remind_interval_minutes: 60,
    target_role: 'tech_owner',
    escalation_enabled: true,
    escalation_after_minutes: null,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    enabled: true,
  },
  {
    id: 5,
    rule_code: 'customer_confirm_timeout',
    rule_name: '客户确认超时',
    stage: 'ticket',
    threshold_minutes: 240,
    remind_interval_minutes: 120,
    target_role: 'service_owner',
    escalation_enabled: true,
    escalation_after_minutes: null,
    escalation_cc_emails: 'qhdet@cleartv.cn',
    enabled: true,
  },
]

// ============== API 方法 ==============
export async function fetchSlaRules(): Promise<SlaRuleItem[]> {
  if (isMockEnabled()) {
    await mockDelay()
    return mockSlaRules.map((item) => ({ ...item }))
  }
  const { data } = await client.get<{ items: SlaRuleItem[] }>('/sla/rules')
  return data.items
}

export async function updateSlaRule(ruleId: number, payload: UpdateSlaRulePayload): Promise<SlaRuleItem> {
  if (isMockEnabled()) {
    await mockDelay()
    const rule = mockSlaRules.find((item) => item.id === ruleId)
    if (!rule) throw Object.assign(new Error('响应时限规则不存在'), { code: 'NOT_FOUND' })
    Object.assign(rule, payload)
    return { ...rule }
  }
  const { data } = await client.put<SlaRuleItem>(`/sla/rules/${ruleId}`, payload)
  return data
}

export async function fetchSlaEvents(params: {
  page?: number
  page_size?: number
  event_status?: string
}): Promise<PaginatedData<SlaEventItem>> {
  if (isMockEnabled()) {
    await mockDelay()
    let filtered = [...mockSlaEvents]
    if (params.event_status) {
      filtered = filtered.filter((e) => e.event_status === params.event_status)
    }
    return paginate(filtered, params.page || 1, params.page_size || 20)
  }
  const { data } = await client.get<PaginatedData<SlaEventItem>>('/sla/events', { params })
  return data
}

export async function fetchSlaEventDetail(eventId: string): Promise<SlaEventDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const detail = mockSlaDetail[eventId]
    if (!detail) throw Object.assign(new Error('超时提醒不存在'), { code: 'NOT_FOUND' })
    return { ...detail }
  }
  const { data } = await client.get<SlaEventDetail>(`/sla/events/${eventId}`)
  return data
}

export async function remindSlaEvent(eventId: string, note?: string): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    return
  }
  await client.post(`/sla/events/${eventId}/remind`, { channel: 'system', remark: note })
}

export async function ignoreSlaEvent(eventId: string, reason: string): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    const e = mockSlaEvents.find((e) => e.id === eventId)
    if (e) e.event_status = 'ignored'
    return
  }
  await client.post(`/sla/events/${eventId}/ignore`, { reason })
}

export async function resolveSlaEvent(eventId: string, note: string): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    const e = mockSlaEvents.find((e) => e.id === eventId)
    if (e) e.event_status = 'resolved'
    return
  }
  await client.post(`/sla/events/${eventId}/resolve`, { reason: note })
}
